import type {
  PortalChannelBlock,
  PortalDelta,
  PortalReportLabels,
  PortalTotals,
} from '../../../../../types/portalReport';
import { buildPortalDeltas, portalMetricTone } from '../../../../../lib/portalReport/deltas';
import { channelUnmeasuredInPeriod } from '../../../../../lib/portalReport/reportCopy';
import type { ChartMetric } from '../../../../../lib/portalReport/chartPlan';
import { Band, Figure } from './controls';
import { DASH, moneyFull, moneyRound, numberFull } from './format';

// CLIENT PORTAL RESTYLE — the hero band.
//
// ⚠️⚠️ THE BAND IS `ink-deep` IN BOTH THEMES, and that is load-bearing. It is
// what lets `volt` work at all — volt on cream is 1.00:1, literally invisible —
// and in light mode it satisfies the no-two-adjacent-light-surfaces law by
// construction. It is also why the deltas may use kelly / error here: those
// work on a DARK surface only, and this surface is always dark.
//
// ⚠️ EXACTLY ONE VOLT ELEMENT PER PAGE: the headline figure. Two bugs were
// caught doing this in the mockup — a volt platform badge repeated on every
// Meta row, and a second volt figure on the Google page.
//
// ⚠️ HERO METRICS ARE LEADS, COST PER LEAD AND SPEND. Impressions and clicks
// appear in tables only, never as a headline (brand rule 7).
//
// ⚠️⚠️ THE CRM VARIANT LIVES IN `CrmHeroBand.tsx` AND IS NOT A `ReportHero`.
// It was `crmHeroFor()` feeding this component until 2026-09-02, when the hero
// and the CRM panel beneath it merged: the merged band carries seven figures in
// two rows, an adjacent leads/internet-leads PAIR, sparklines, a caveat, a
// stated comparison basis and the nesting bar. Bending this component into that
// shape would have made every branch here conditional on a CRM that most reports
// do not have. `DeltaLine` and `HeroStat` are SHARED, so the two agree about how
// a delta is toned and drawn.

export interface HeroStat {
  label: string;
  value: string | null;
  delta: PortalDelta | null;
  /** ⚠️ Decides the delta's TONE, through the one `metricDirection`. */
  metric: ChartMetric;
  /** Shown INSTEAD of a percentage when there is no honest comparison. */
  unavailable: string | null;
}

export function ReportHero({
  eyebrow,
  headline,
  intro,
  stats,
}: {
  eyebrow: string;
  headline: string;
  intro: string;
  stats: HeroStat[];
}) {
  return (
    <Band variant="hero">
      <div className="rpt-hero">
        <div>
          <span className="rpt-mono">{eyebrow}</span>
          <p className="rpt-hero-figure">{headline}</p>
          <p className="rpt-sub rpt-muted">{intro}</p>
        </div>
        <div className="rpt-stat-grid">
          {stats.map((s) => (
            <p key={s.label} className="rpt-stat">
              <Figure value={s.value} className="rpt-stat-figure" />
              <span className="rpt-mono rpt-stat-label">{s.label}</span>
              <DeltaLine stat={s} />
            </p>
          ))}
        </div>
      </div>
    </Band>
  );
}

/**
 * ⚠️⚠️ THE DIRECTION IS NOT THE JUDGEMENT. A falling cost per lead is the best
 * news on the page; showing it red tells the client the opposite of the truth.
 * The tone comes from `portalMetricTone` — the same vocabulary the staff
 * dashboards use — and the ARROW comes from the direction, so the two are
 * allowed to disagree, which is exactly the point.
 *
 * ⚠️ A missing comparison shows the REASON, never a fabricated −100%.
 */
export function DeltaLine({ stat }: { stat: HeroStat }) {
  if (!stat.delta) {
    return stat.unavailable ? (
      <span className="rpt-delta rpt-tone-neutral">{stat.unavailable}</span>
    ) : (
      <span className="rpt-delta rpt-tone-neutral">{DASH}</span>
    );
  }
  const tone = portalMetricTone(stat.metric);
  const dir = stat.delta.direction;
  const good =
    dir === 'flat' || tone === 'neutral'
      ? 'neutral'
      : (dir === 'up') === (tone === 'good-up')
        ? 'good'
        : 'bad';
  // ⚠️ A TRUE MINUS SIGN and an arrow, so the direction survives without colour
  // — which is what the light-card rule needs and what a colour-blind reader
  // needs everywhere.
  const arrow = dir === 'up' ? '▲' : dir === 'down' ? '▼' : '■';
  const sign = stat.delta.pct < 0 ? '−' : '+';
  return (
    <span className={`rpt-delta rpt-tone-${good}`}>
      {arrow} {sign}
      {Math.abs(stat.delta.pct).toFixed(0)}%
    </span>
  );
}

/**
 * Build the hero stats for one channel block.
 *
 * ⚠️ MONEY IS PER CURRENCY AND NEVER BLENDED. A single-currency scope — which
 * is every scope in practice — renders one figure; a genuinely mixed one
 * renders both, separated, rather than summing AUD into NZD to produce a number
 * that exists in no currency at all.
 */
export function heroStatsFor(
  block: PortalChannelBlock,
  labels: PortalReportLabels,
): { headline: string; stats: HeroStat[] } {
  const deltas = buildPortalDeltas(
    block.totals,
    block.comparison?.totals ?? [],
    block.comparison ? block.comparison.unavailableReason : 'No comparison period was selected.',
  );
  return {
    headline: block.totals.length ? numberFull(sumLeads(block.totals)) : DASH,
    stats: [
      {
        label: labels.costPerLead,
        value: perCurrency(block.totals, (t) =>
          t.costPerLead === null ? null : moneyFull(t.costPerLead, t.currency),
        ),
        delta: deltas.costPerLead,
        metric: 'costPerLead',
        unavailable: deltas.unavailableReason,
      },
      {
        label: labels.spend,
        value: perCurrency(block.totals, (t) => moneyRound(t.spend, t.currency)),
        delta: deltas.spend,
        metric: 'spend',
        unavailable: deltas.unavailableReason,
      },
    ],
  };
}

/**
 * One channel's LEADS delta for the hero's per-channel breakdown (§3, 2026-09-03).
 *
 * ⚠️⚠️ THIS IS THE SAME `buildPortalDeltas` CALL `heroStatsFor` MAKES, on the
 * same block shape, so a channel's change and the combined change cannot come
 * to mean different things. The stats were built with `delta: null` hard-coded
 * until now — nothing was missing from the payload; `assembleBlock` has always
 * built a `comparison` for meta, google AND combined, each with its own totals
 * and `unavailableReason`.
 *
 * ⚠️⚠️ AN UNMEASURED CHANNEL GETS NO DELTA AT ALL. Its VALUE is already an em
 * dash, and `buildPortalDeltas` would happily compare an empty current period
 * against a real prior one and report −100% — telling a client a channel
 * collapsed when the truth is we have no figures for it. That is rule 15 wearing
 * a percentage, and it is Google's live situation while its backfill lands.
 *
 * ⚠️ The REASON goes in `unavailable`, which already exists and is already
 * rendered by `DeltaLine`. A bare dash where a change should be reads as a
 * fault; a sentence reads as a fact about the data.
 */
export function channelLeadsDelta(
  block: PortalChannelBlock,
  channelLabel: string,
): { delta: PortalDelta | null; unavailable: string | null } {
  if (block.totals.length === 0) {
    return { delta: null, unavailable: channelUnmeasuredInPeriod(channelLabel) };
  }
  const deltas = buildPortalDeltas(
    block.totals,
    block.comparison?.totals ?? [],
    block.comparison ? block.comparison.unavailableReason : 'No comparison period was selected.',
  );
  return { delta: deltas.leads, unavailable: deltas.leads ? null : deltas.unavailableReason };
}

export function sumLeads(totals: readonly PortalTotals[]): number {
  // ⚠️ Counts blend across currencies quite safely — a lead is a lead whoever
  // paid. Only MONEY may not.
  return totals.reduce((sum, t) => sum + t.leads, 0);
}

/**
 * Render one figure per currency, joined.
 *
 * ⚠️ Returns null when NOTHING is measurable, so the caller renders an em dash
 * rather than an empty string that reads as a styled zero.
 */
export function perCurrency(
  totals: readonly PortalTotals[],
  render: (t: PortalTotals) => string | null,
): string | null {
  const parts = totals.map(render).filter((s): s is string => s !== null);
  return parts.length ? parts.join(' · ') : null;
}
